<template>
  <form class="aicc" :class="{ 'is-over': dragOver }" @submit.prevent="submit" @dragover.prevent="dragOver = true" @dragleave="dragOver = false" @drop.prevent="onDrop">
    <div v-if="files.length" class="aicc__previews">
      <div v-for="(file, i) in files" :key="file.key" class="aicc__preview">
        <img :src="file.url" :alt="file.name" />
        <button type="button" class="aicc__remove" :aria-label="`Remove ${file.name}`" @click="remove(i)">×</button>
      </div>
      <div v-if="isDraw" class="aicc__role">
        <template v-if="latestDesignVersion && !fresh">
          <span>Will be <strong>added</strong> to your latest picture (v{{ latestDesignVersion }}) as you describe.</span>
        </template>
        <template v-else>
          <button type="button" :class="{ 'is-on': imagesRole === 'base' }" @click="imagesRole = 'base'">Draw in this photo</button>
          <button type="button" :class="{ 'is-on': imagesRole === 'reference' }" @click="imagesRole = 'reference'">It's a piece to add</button>
          <span>{{ imagesRole === 'base' ? (files.length > 1 ? 'The first photo is the room; the others are pieces to add.' : 'Your photo stays as it is — only what you ask for changes.') : 'A new picture is made with this piece in it.' }}</span>
        </template>
      </div>
    </div>
    <div v-if="designEnabled" class="aicc__modes" role="tablist" aria-label="What do you want to do?">
      <button type="button" role="tab" :aria-selected="!isDraw" class="aicc__mode" :class="{ 'is-on': !isDraw }" @click="setMode('ask')">
        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a8 8 0 0 1-11.6 7.1L4 21l1.9-5.4A8 8 0 1 1 21 12z" /></svg>
        Ask
      </button>
      <button type="button" role="tab" :aria-selected="isDraw" class="aicc__mode" :class="{ 'is-on': isDraw }" :disabled="designDisabled" @click="setMode('draw')">
        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l1.8 4.7 4.7 1.8-4.7 1.8L12 16l-1.8-4.7L5.5 9.5l4.7-1.8z" /><path d="M5 19l1-2.5L8.5 15.5 6 16.5z" /></svg>
        Draw my idea
      </button>
      <span v-if="isDraw" class="aicc__mode-note">
        <template v-if="latestDesignVersion && !fresh">Your next words <strong>change</strong> your latest picture (v{{ latestDesignVersion }}). <button type="button" class="aicc__link" @click="fresh = true">Start a new design</button></template>
        <template v-else>Describe a piece or a room and I make a picture of it — or attach a photo of your room to draw in it. Uses 1 of your pictures for today.</template>
      </span>
    </div>
    <div class="aicc__bar">
      <label class="aicc__attach" :class="{ 'is-disabled': disabled || photosDisabled || files.length >= 3 }" :title="photosDisabled ? 'Photo limit reached for today' : (isDraw ? 'Attach a photo to draw in it, or a picture of a piece to add' : 'Attach a photo (your room, a piece you like)')">
        <input type="file" accept="image/jpeg,image/png,image/webp" multiple :disabled="disabled || photosDisabled || files.length >= 3" @change="onPick" />
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2" /><circle cx="9" cy="10" r="1.6" /><path d="M21 16l-5-5-7 7" /></svg>
      </label>
      <textarea
        ref="input"
        v-model="text"
        class="aicc__input"
        rows="1"
        maxlength="1500"
        :placeholder="isDraw ? (latestDesignVersion && !fresh ? 'What should change? e.g. dark green doors, add an island, brass handles…' : 'Describe it: e.g. a walk-in wardrobe in light oak, matt white doors, LED strips, modern and minimal') : placeholder"
        :disabled="disabled"
        @keydown.enter.exact.prevent="submit"
        @input="autoGrow"
        @paste="onPaste"
      ></textarea>
      <button type="submit" class="aicc__send" :class="{ 'is-draw': isDraw }" :disabled="!canSend" :aria-label="isDraw ? 'Draw it' : 'Send'" :title="isDraw ? 'Draw it' : 'Send'">
        <svg v-if="isDraw" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l1.8 4.7 4.7 1.8-4.7 1.8L12 16l-1.8-4.7L5.5 9.5l4.7-1.8z" /><path d="M5 19l1-2.5L8.5 15.5 6 16.5z" /></svg>
        <svg v-else viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" /></svg>
      </button>
    </div>
    <p v-if="hint" class="aicc__hint">{{ hint }}</p>
  </form>
</template>

<script setup>
import { computed, defineEmits, defineExpose, defineProps, onBeforeUnmount, ref } from 'vue';

const props = defineProps({
  disabled: { type: Boolean, default: false },
  photosDisabled: { type: Boolean, default: false },
  placeholder: { type: String, default: 'Ask anything, or attach a photo of your room…' },
  hint: { type: String, default: '' },
  designEnabled: { type: Boolean, default: false },
  designDisabled: { type: Boolean, default: false },
  latestDesignVersion: { type: Number, default: 0 },
});
const emit = defineEmits(['send', 'design']);

const mode = ref('ask');
const fresh = ref(false); // Draw mode: next description starts a new design instead of changing the latest
const imagesRole = ref('base'); // Draw mode, no picture yet: attached photo is the room to draw in ('base') or a piece to add ('reference')
const isDraw = computed(() => props.designEnabled && mode.value === 'draw');
function setMode(value) {
  mode.value = value;
  input.value?.focus();
}

const text = ref('');
const files = ref([]);
const dragOver = ref(false);
const input = ref(null);

const canSend = computed(() => !props.disabled && (isDraw.value
  ? !props.designDisabled && (text.value.trim().length >= 3 || (files.value.length > 0 && text.value.trim().length > 0))
  : text.value.trim().length > 0 || files.value.length > 0));

function addFiles(list) {
  Array.from(list || [])
    .filter((file) => file && /^image\/(jpeg|png|webp)$/.test(file.type))
    .slice(0, 3 - files.value.length)
    .forEach((file) => files.value.push({ file, name: file.name, url: URL.createObjectURL(file), key: `${file.name}-${file.size}-${Date.now()}` }));
}
function onPick(event) {
  addFiles(event.target.files);
  event.target.value = '';
  input.value?.focus();
}
function onDrop(event) {
  dragOver.value = false;
  if (!props.disabled) addFiles(event.dataTransfer?.files);
}
function onPaste(event) {
  const pasted = Array.from(event.clipboardData?.files || []);
  if (pasted.length) {
    event.preventDefault();
    addFiles(pasted);
  }
}
function remove(index) {
  URL.revokeObjectURL(files.value[index].url);
  files.value.splice(index, 1);
}
function autoGrow() {
  const el = input.value;
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
}
function submit() {
  if (!canSend.value) return;
  if (isDraw.value) {
    emit('design', { text: text.value.trim(), fresh: fresh.value || !props.latestDesignVersion, files: files.value.map((f) => f.file), imagesRole: imagesRole.value });
    fresh.value = false;
    imagesRole.value = 'base';
    text.value = '';
    files.value.forEach((f) => URL.revokeObjectURL(f.url));
    files.value = [];
    autoGrow();
    return;
  }
  emit('send', { text: text.value, files: files.value.map((f) => f.file) });
  text.value = '';
  files.value.forEach((f) => URL.revokeObjectURL(f.url));
  files.value = [];
  autoGrow();
}
function focus() {
  input.value?.focus();
}
function setText(value) {
  text.value = value;
  autoGrow();
}
onBeforeUnmount(() => files.value.forEach((f) => URL.revokeObjectURL(f.url)));
defineExpose({ focus, setText, setMode });
</script>

<style>
.aicc { border-top: 1px solid #e8e9ea; background: #fff; padding: 10px 12px; transition: background 0.15s ease; }
.aicc.is-over { background: rgba(187, 151, 109, 0.1); }
.aicc__previews { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 8px; }
.aicc__preview { position: relative; }
.aicc__preview img { width: 64px; height: 64px; border-radius: 10px; object-fit: cover; border: 1px solid #e8e9ea; display: block; }
.aicc__remove { position: absolute; top: -6px; right: -6px; width: 20px; height: 20px; border-radius: 999px; border: 0; background: #172430; color: #fff; font-size: 14px; line-height: 20px; cursor: pointer; }
.aicc__bar { display: flex; align-items: flex-end; gap: 8px; }
.aicc__attach { flex: 0 0 42px; height: 42px; border-radius: 12px; border: 1px solid #dfe2e4; display: inline-flex; align-items: center; justify-content: center; color: #3c474e; cursor: pointer; background: #fff; }
.aicc__attach:hover { border-color: #bb976d; color: #bb976d; }
.aicc__attach.is-disabled { opacity: 0.45; cursor: not-allowed; }
.aicc__attach input { display: none; }
.aicc__input { flex: 1; resize: none; box-sizing: border-box; height: 42px; min-height: 42px; max-height: 140px; padding: 10px 12px; border: 1px solid #dfe2e4; border-radius: 12px; font-size: 14px; line-height: 1.45; color: #172430; outline: none; background: #fff; font-family: inherit; }
.aicc__input:focus { border-color: #bb976d; box-shadow: 0 0 0 3px rgba(187, 151, 109, 0.18); }
.aicc__send { flex: 0 0 42px; height: 42px; border-radius: 12px; border: 0; background: #bb976d; color: #fff; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; }
.aicc__role { flex-basis: 100%; display: flex; flex-wrap: wrap; align-items: center; gap: 6px; font-size: 12px; color: #8a6a3e; }
.aicc__role button { height: 26px; padding: 0 10px; border-radius: 999px; border: 1px solid #dccdb9; background: #fff; color: #8a6a3e; font-size: 11.5px; font-weight: 600; cursor: pointer; }
.aicc__role button.is-on { background: #172430; border-color: #172430; color: #fff; }
.aicc__role span { flex: 1 1 200px; }
.dark .aicc__role button { background: #22313d; border-color: #3c474e; color: #dccdb9; }
.dark .aicc__role button.is-on { background: #bb976d; border-color: #bb976d; }
.aicc__modes { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin-bottom: 8px; }
.aicc__mode { display: inline-flex; align-items: center; gap: 6px; height: 30px; padding: 0 12px; border-radius: 999px; border: 1px solid #e3e5e6; background: #fff; color: #3c474e; font-size: 12.5px; font-weight: 600; cursor: pointer; }
.aicc__mode.is-on { background: #172430; border-color: #172430; color: #fff; }
.aicc__mode:disabled { opacity: 0.5; cursor: default; }
.aicc__mode-note { flex: 1 1 240px; font-size: 12px; color: #8a6a3e; }
.aicc__link { border: 0; background: none; padding: 0; color: #bb976d; font: inherit; font-weight: 600; text-decoration: underline; cursor: pointer; }
.aicc__send.is-draw { background: #bb976d; }
.dark .aicc__mode { background: #22313d; border-color: #3c474e; color: #fff; }
.dark .aicc__mode.is-on { background: #bb976d; border-color: #bb976d; }
.aicc__send:hover { background: #172430; }
.aicc__send:disabled { opacity: 0.4; cursor: not-allowed; background: #bb976d; }
.aicc__hint { margin: 8px 2px 0; font-size: 10.5px; line-height: 1.4; color: #a3aab0; }

.dark .aicc { background: #1e2a35; border-color: #3c474e; }
.dark .aicc__input, .dark .aicc__attach { background: #172430; border-color: #3c474e; color: #fff; }
.dark .aicc__preview img { border-color: #3c474e; }
</style>
